import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { 
  listMerchantBusinessInfos, 
  getMerchantBusinessInfoById,
  addMerchantBusinessInfo,
  updateMerchantBusinessInfo,
  deleteMerchantBusinessInfoById
} from '../../api/merchantBusinessInfo';
import { message } from 'antd';

// 分页查询商家经营信息列表的异步 thunk
export const fetchMerchantBusinessInfos = createAsyncThunk(
  'merchantBusinessInfo/fetchList',
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await listMerchantBusinessInfos(params);
      return response.data;
    } catch (error) {
      message.error('获取商家经营信息列表失败');
      return rejectWithValue(error.response?.data);
    }
  }
);

// 获取商家经营信息详情的异步 thunk
export const fetchMerchantBusinessInfoDetail = createAsyncThunk(
  'merchantBusinessInfo/fetchDetail',
  async (id, { rejectWithValue }) => {
    try {
      const response = await getMerchantBusinessInfoById(id);
      return response.data;
    } catch (error) {
      message.error('获取商家经营信息详情失败');
      return rejectWithValue(error.response?.data);
    }
  }
);

// 新增商家经营信息的异步 thunk
export const createMerchantBusinessInfo = createAsyncThunk(
  'merchantBusinessInfo/create',
  async (data, { dispatch, rejectWithValue }) => {
    try {
      const response = await addMerchantBusinessInfo(data);
      
      if (response.code === 200) {
        message.success('新增商家经营信息成功');
        // 重新获取列表
        dispatch(fetchMerchantBusinessInfos());
        return response.data;
      } else {
        message.error(response.message || '新增失败');
        return rejectWithValue(response);
      }
    } catch (error) {
      message.error('新增商家经营信息请求失败');
      return rejectWithValue(error.response?.data);
    }
  }
);

// 更新商家经营信息的异步 thunk
export const updateMerchantBusinessInfoAction = createAsyncThunk(
  'merchantBusinessInfo/update',
  async (data, { dispatch, rejectWithValue }) => {
    try {
      const response = await updateMerchantBusinessInfo(data);
      
      if (response.code === 200) {
        message.success('更新商家经营信息成功');
        // 重新获取列表
        dispatch(fetchMerchantBusinessInfos());
        return response.data;
      } else {
        message.error(response.message || '更新失败');
        return rejectWithValue(response);
      }
    } catch (error) {
      message.error('更新商家经营信息请求失败');
      return rejectWithValue(error.response?.data);
    }
  }
);

// 删除商家经营信息的异步 thunk
export const deleteMerchantBusinessInfoAction = createAsyncThunk(
  'merchantBusinessInfo/delete',
  async (id, { dispatch, rejectWithValue }) => {
    try {
      const response = await deleteMerchantBusinessInfoById(id);
      
      if (response.code === 200) {
        message.success('删除商家经营信息成功');
        // 重新获取列表
        dispatch(fetchMerchantBusinessInfos());
        return response.data;
      } else {
        message.error(response.message || '删除失败');
        return rejectWithValue(response);
      }
    } catch (error) {
      message.error('删除商家经营信息请求失败');
      return rejectWithValue(error.response?.data);
    }
  }
);

const merchantBusinessInfoSlice = createSlice({
  name: 'merchantBusinessInfo',
  initialState: {
    businessInfos: [],
    total: 0,
    page: 1,
    pageSize: 10,
    loading: false,
    error: null,
    currentBusinessInfo: null
  },
  reducers: {
    setPage: (state, action) => {
      state.page = action.payload;
    },
    setPageSize: (state, action) => {
      state.pageSize = action.payload;
    },
    setCurrentBusinessInfo: (state, action) => {
      state.currentBusinessInfo = action.payload;
    }
  },
  extraReducers: (builder) => {
    builder
      // 获取列表
      .addCase(fetchMerchantBusinessInfos.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchMerchantBusinessInfos.fulfilled, (state, action) => {
        state.loading = false;
        state.businessInfos = action.payload.records;
        state.total = action.payload.total;
        state.page = action.payload.current;
        state.pageSize = action.payload.size;
      })
      .addCase(fetchMerchantBusinessInfos.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // 获取详情
      .addCase(fetchMerchantBusinessInfoDetail.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchMerchantBusinessInfoDetail.fulfilled, (state, action) => {
        state.loading = false;
        state.currentBusinessInfo = action.payload;
      })
      .addCase(fetchMerchantBusinessInfoDetail.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // 新增
      .addCase(createMerchantBusinessInfo.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createMerchantBusinessInfo.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(createMerchantBusinessInfo.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // 更新
      .addCase(updateMerchantBusinessInfoAction.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateMerchantBusinessInfoAction.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(updateMerchantBusinessInfoAction.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // 删除
      .addCase(deleteMerchantBusinessInfoAction.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteMerchantBusinessInfoAction.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(deleteMerchantBusinessInfoAction.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  }
});

export const { 
  setPage, 
  setPageSize, 
  setCurrentBusinessInfo 
} = merchantBusinessInfoSlice.actions;

export default merchantBusinessInfoSlice.reducer;
